import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, GraduationCap, Briefcase, ListChecks, ArrowRight, FileUp } from "lucide-react";
import { api, isoDay } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, StatCard, BeltBadge, StatusPill } from "../../components/common";
import { MembershipCard } from "../../components/Membership";
import { ContinueLearning } from "../../components/ContinueLearning";

export default function StaffHome() {
  const { user } = useAuth();
  const [d, setD] = useState({ profile: null, att: null, apps: [], tasks: [], enr: [] });

  const load = () => {
    const today = isoDay(new Date());
    Promise.all([api.get("/profile"), api.get("/attendance/status"), api.get("/my/applications"), api.get(`/tasks?start=${today}&end=${today}`), api.get("/my/enrollments")])
      .then(([p, a, ap, t, e]) => setD({ profile: p.data, att: a.data, apps: ap.data, tasks: t.data, enr: e.data }));
  };
  useEffect(() => { load(); }, []);

  const p = d.profile;
  return (
    <div>
      <PageHeader eyebrow="Staff dashboard" title={`Akwaaba, ${user.name.split(" ")[0]}`} subtitle="Train, get certified and manage your work day in one place." />
      {p && !p.membership_paid && <div className="mb-6"><MembershipCard onPaid={load} /></div>}
      <ContinueLearning />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard accent label="Shift status" value={<StatusPill status={d.att?.state || "off"} testid="home-shift-status" />} icon={Clock} testid="stat-shift" />
        <StatCard label="Certifications" value={p?.certifications?.length ?? 0} icon={GraduationCap} testid="stat-certs" />
        <StatCard label="Applications" value={d.apps.length} icon={Briefcase} testid="stat-apps" />
        <StatCard label="Tasks today" value={d.tasks.length} icon={ListChecks} testid="stat-tasks" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        <div className="dx-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold text-lg">Your training path</h3>
            <BeltBadge belt={p?.highest_belt} testid="home-highest-belt" />
          </div>
          <div className="mt-5 grid sm:grid-cols-3 gap-3">
            {["yellow-belt", "green-belt", "black-belt"].map((slug) => {
              const e = d.enr.find((x) => x.course_slug === slug);
              return (
                <div key={slug} className="rounded-xl border p-4" data-testid={`home-path-${slug}`}>
                  <div className="font-semibold capitalize">{slug.replace("-", " ")}</div>
                  <div className="mt-2"><StatusPill status={e ? (e.status === "completed" ? "done" : "in_progress") : "pending"} /></div>
                </div>
              );
            })}
          </div>
          <Link to="/app/academy" className="dx-btn-purple mt-6" data-testid="home-academy-link">Go to Academy <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="dx-card p-6">
          <h3 className="font-display font-semibold text-lg">Quick actions</h3>
          <div className="mt-4 space-y-2.5">
            <Link to="/app/attendance" className="dx-btn-gold w-full" data-testid="home-clock-link"><Clock className="w-4 h-4" />Clock in / out</Link>
            <Link to="/app/resume" className="dx-btn-ghost w-full" data-testid="home-resume-link"><FileUp className="w-4 h-4" />{p?.resume_filename ? "Update resume" : "Upload resume"}</Link>
            <Link to="/app/jobs" className="dx-btn-ghost w-full" data-testid="home-jobs-link"><Briefcase className="w-4 h-4" />Browse jobs</Link>
          </div>
        </div>
      </div>

      <div className="dx-card p-6 mt-5">
        <h3 className="font-display font-semibold text-lg mb-4">Recent applications</h3>
        {d.apps.length === 0 ? <p className="text-sm text-[#6b5a73]">No applications yet.</p> : (
          <div className="divide-y">
            {d.apps.slice(0, 5).map((a) => (
              <div key={a.id} className="py-3 flex items-center justify-between gap-3" data-testid={`home-app-${a.id}`}>
                <div><div className="font-semibold">{a.job_title}</div><div className="text-xs text-[#6b5a73]">{a.company_name}{a.source === "automatch" ? " · Matched to you" : ""}</div></div>
                <StatusPill status={a.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
